/* Product card colour swatches (snippets/wg-card-swatches.liquid).
   1. Click: swaps the card image, points the card links at that variant and moves the
      EasySell COD host form to the same variant. No reload. Delegated, so cards added
      later (filters, rails) work too.
   2. Colour: the server paints a colour derived from the variant name as a first-paint
      fallback. Here each swatch is repainted from the variant photo itself: the dial
      (image centre) and the case metal (dominant other tone) as a split gradient, so
      "15407BC / Black Dial" shows white gold + black rather than plain black. */
(() => {
  if (window.__wgCardSwatches) return;
  window.__wgCardSwatches = true;

  const preloaded = new Set();
  const colourCache = new Map();
  const CACHE_KEY = 'wg-swatch-colours';
  try {
    const stored = JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}');
    Object.entries(stored).forEach(([k, v]) => colourCache.set(k, v));
  } catch (e) {
    /* no storage */
  }
  const persist = () => {
    try {
      const obj = {};
      colourCache.forEach((v, k) => (obj[k] = v));
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(obj));
    } catch (e) {
      /* ignore */
    }
  };

  /* ---------- image swap ---------- */
  const preload = (swatch) => {
    const src = swatch.dataset.src;
    if (!src || preloaded.has(src)) return;
    preloaded.add(src);
    const img = new Image();
    if (swatch.dataset.srcset) img.srcset = swatch.dataset.srcset;
    img.sizes = swatch.closest('.wg-pc')?.querySelector('.wg-pc__img')?.sizes || '';
    img.src = src;
  };

  const select = (swatch) => {
    const card = swatch.closest('.wg-pc');
    if (!card) return;
    card.querySelectorAll('[data-wg-swatch]').forEach((el) => el.setAttribute('aria-pressed', String(el === swatch)));
    const img = card.querySelector('.wg-pc__img:not(.wg-pc__img--secondary)');
    if (img && swatch.dataset.src) {
      if (swatch.dataset.srcset) img.srcset = swatch.dataset.srcset;
      img.src = swatch.dataset.src;
      img.removeAttribute('loading');
      card.classList.add('wg-pc--swatched');
    }
    const url = swatch.dataset.url;
    if (url) card.querySelectorAll('.wg-pc__link, a.wg-pc__atc').forEach((link) => (link.href = url));
    const variantId = swatch.dataset.variantId;
    if (variantId) card.querySelectorAll('.wg-pc__cod-host input[name="id"]').forEach((input) => (input.value = variantId));
  };

  document.addEventListener('click', (event) => {
    const swatch = event.target.closest('[data-wg-swatch]');
    if (!swatch) return;
    event.preventDefault();
    event.stopPropagation();
    select(swatch);
  });

  ['pointerover', 'focusin'].forEach((type) => {
    document.addEventListener(
      type,
      (event) => {
        const swatch = event.target.closest?.('[data-wg-swatch]');
        if (swatch) preload(swatch);
      },
      { passive: true }
    );
  });

  /* ---------- colour sampling ---------- */
  const SIZE = 48;
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });

  const rgbToHex = ([r, g, b]) => '#' + [r, g, b].map((n) => Math.round(n).toString(16).padStart(2, '0')).join('');
  const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
  const pixelAt = (data, x, y) => {
    const i = (y * SIZE + x) * 4;
    return [data[i], data[i + 1], data[i + 2], data[i + 3]];
  };
  const avg = (px) => {
    if (!px.length) return null;
    const s = px.reduce((acc, p) => [acc[0] + p[0], acc[1] + p[1], acc[2] + p[2]], [0, 0, 0]);
    return s.map((n) => n / px.length);
  };

  const analyse = (img) => {
    ctx.clearRect(0, 0, SIZE, SIZE);
    // centre-crop to a square so the watch head stays in the middle
    const side = Math.min(img.naturalWidth, img.naturalHeight);
    const sx = (img.naturalWidth - side) / 2;
    const sy = (img.naturalHeight - side) / 2;
    ctx.drawImage(img, sx, sy, side, side, 0, 0, SIZE, SIZE);
    const { data } = ctx.getImageData(0, 0, SIZE, SIZE);
    const c = SIZE / 2;
    // Background = the corners of the photo (white, cream or dark studio sweep)
    const corners = [pixelAt(data, 1, 1), pixelAt(data, SIZE - 2, 1), pixelAt(data, 1, SIZE - 2), pixelAt(data, SIZE - 2, SIZE - 2)];
    const isBackground = (p) => p[3] < 200 || corners.some((k) => k[3] >= 200 && dist(p, k) < 22);
    const dialPx = [];
    const bodyPx = [];
    for (let y = 0; y < SIZE; y++) {
      for (let x = 0; x < SIZE; x++) {
        const p = pixelAt(data, x, y);
        if (p[3] < 200) continue;
        const r = Math.hypot(x - c, y - c) / c;
        // The dial sits at the image centre in product shots; keep it even when it is white
        if (r < 0.14) dialPx.push(p);
        else if (r < 0.75 && !isBackground(p)) bodyPx.push(p);
      }
    }
    const dial = avg(dialPx);
    if (!dial) return null;

    // Case metal: dominant tone among body pixels that differs clearly from the dial
    const bins = new Map();
    bodyPx.forEach((p) => {
      if (dist(p, dial) < 60) return;
      const key = [p[0] >> 4, p[1] >> 4, p[2] >> 4].join(',');
      const bin = bins.get(key) || { n: 0, sum: [0, 0, 0] };
      bin.n++;
      bin.sum[0] += p[0];
      bin.sum[1] += p[1];
      bin.sum[2] += p[2];
      bins.set(key, bin);
    });
    let best = null;
    bins.forEach((bin) => {
      if (!best || bin.n > best.n) best = bin;
    });
    const minShare = bodyPx.length * 0.06;
    const metal = best && best.n >= minShare ? best.sum.map((n) => n / best.n) : null;
    return { dial: rgbToHex(dial), metal: metal ? rgbToHex(metal) : null };
  };

  const paint = (swatch, colours) => {
    const fill = swatch.querySelector('.wg-pc__swatch-fill');
    if (!fill || !colours) return;
    const bg = colours.metal
      ? `linear-gradient(135deg, ${colours.metal} 0 48%, ${colours.dial} 52% 100%)`
      : colours.dial;
    fill.style.background = bg;
    swatch.classList.add('wg-pc__swatch--sampled');
  };

  const sampleSwatch = (swatch) => {
    if (swatch.dataset.sampled) return;
    swatch.dataset.sampled = '1';
    const src = swatch.dataset.sampleSrc || swatch.dataset.src;
    if (!src || !ctx) return;
    if (colourCache.has(src)) {
      paint(swatch, colourCache.get(src));
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = () => {
      try {
        const colours = analyse(img);
        if (colours) {
          colourCache.set(src, colours);
          persist();
          paint(swatch, colours);
        }
      } catch (e) {
        /* tainted canvas or decode issue: keep the name-based colour */
      }
    };
    img.src = src;
  };

  const scan = (root) => {
    (root.querySelectorAll ? root : document).querySelectorAll('[data-wg-swatch]').forEach(sampleSwatch);
  };

  const io =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              io.unobserve(entry.target);
              scan(entry.target);
            });
          },
          { rootMargin: '200px' }
        )
      : null;

  const observeCards = (root = document) => {
    root.querySelectorAll('.wg-pc__swatches:not(.wg-pc__swatches--empty)').forEach((group) => {
      if (group.dataset.wgObserved) return;
      group.dataset.wgObserved = '1';
      if (io) io.observe(group);
      else scan(group);
    });
  };

  const start = () => {
    observeCards();
    new MutationObserver((records) => {
      for (const r of records) if (r.addedNodes.length) return observeCards();
    }).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
